import React, { MouseEvent as ReactMouseEvent, FunctionComponent, ReactNode } from 'react'
import { ButtonProps } from 'semantic-ui-react'
import { differenceInCalendarDays, format } from 'date-fns'
import PhoneNumber from 'awesome-phonenumber'
import { Msisdn, OnlyProps } from 'src/types'
import { PlanTerm } from 'common/Products/types'

export enum Keys {
  UP = 38,
  RIGHT = 39,
  DOWN = 37,
  LEFT = 40,
}

export enum Colours {
  TEAL = '#39A486',
  GREY = '#77818B',
  MEDIUM_GREY = '#BBBBBB',
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/Reduce
// @ts-ignore
export const groupBy = (objectArray, property) => {
  // @ts-ignore
  return objectArray.reduce((acc, obj) => {
    const key = obj[property]
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(obj)
    return acc
  }, {})
}

// Chunk arrays into a smaller array per chunk size
// https://stackoverflow.com/questions/8495687/split-array-into-chunks
export function chunk<T>(arr: T[], chunkSize: number): T[][] {
  const R: T[][] = []
  for (let i = 0, len = arr.length; i < len; i += chunkSize) R.push(arr.slice(i, i + chunkSize))
  return R
}

export const formatMsisdn = (phone: Msisdn | string): string => {
  const normalised = phone.replace(/^00(?=[1-9])/, '+')
  const parsed = new PhoneNumber(normalised, 'NZ')
  if (!parsed.isValid()) return normalised
  if (parsed.getRegionCode() === 'NZ') return parsed.getNumber('national')
  return parsed.getNumber('international')
}

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>

export type ClickEvent = (
  event: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
  data: ButtonProps
) => void

export const formatPrice = (initialValue: string | number): string => {
  const stringValue = initialValue.toString()
  const isNegative = stringValue.substr(0, 1) === '-'

  const value = parseFloat(isNegative ? stringValue.substr(1) : stringValue)
  if (isNegative) {
    return `-$${value.toFixed(2)}`
  }

  return `$${value.toFixed(2)}`
}

export const formatDate = (date: Date): string => {
  return format(date, 'DD MMM YYYY')
}

export const formatLongDate = (date: Date): string => {
  return format(date, 'D MMMM YYYY')
}

export const formatDateTime = (date: Date): string => {
  return format(date, 'DD MMM hh:mm A')
}

export const formatShortDate = (date: Date): string => {
  return format(date, 'DD MMM')
}

export const formatTime = (date: Date): string => {
  return format(date, 'hh:mm A')
}

export const isNil = (children: React.ReactNode) =>
  children === null || children === undefined || (Array.isArray(children) && children.length === 0)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findByType = (children: any, component: any) => {
  const result: React.ReactNode[] = []
  /* This is the array of result since can have multiple times the same sub-component */
  const type = [component.displayName] || [component.name]
  /* We can store the actual name of the component through the displayName or name property of our sub-component */
  React.Children.forEach(children, (child) => {
    const childType = child && child.type && (child.type.displayName || child.type.name)
    if (type.includes(childType)) {
      result.push(child)
    }
  })
  /* Then we go through each React children, if one of matches the name
  of the sub-component we’re looking for we put it in the result array */
  return result[0]
}

export const formatDaysRemaining = (cycleEnd: Date): number => {
  // cycleEnd is the *exclusive* end of the billing cycle - the date the plan renews or
  // expires (subscriptions fall due once `end` is in the past, so the last day the
  // customer is served on this cycle is the day before). Counting calendar days up to
  // it therefore already counts today and correctly excludes the renewal day itself.
  return differenceInCalendarDays(cycleEnd, Date.now())
}

/**
 * The fixed plan lengths customers choose between on the plan slider, in order. A plan
 * belongs to one by product.term (a 365 day plan and a 12 month plan are both 'year').
 * Rename a length here, e.g. to "Monthly" / "Yearly".
 */
export const PLAN_TERMS: { term: PlanTerm; label: string; position: number }[] = [
  { term: 'month', label: '1 month', position: 0 },
  { term: 'year', label: '1 year', position: 100 },
]

// Day-based lengths and the months they stand for (as product.term has it), so a 30 day
// plan reads "1 month" and "per month"
const DAYS_AS_MONTHS: Record<number, number> = { 30: 1, 365: 12 }

export const formatLength = (length: number, unit?: string): string => {
  if (unit !== 'MONTH' && DAYS_AS_MONTHS[length])
    return formatLength(DAYS_AS_MONTHS[length], 'MONTH')
  if (unit === 'MONTH') {
    if (length % 12 === 0) {
      const years = length / 12
      return `${years} ${years === 1 ? 'year' : 'years'}`
    }
    return `${length} ${length === 1 ? 'month' : 'months'}`
  }
  return `${length} ${length === 1 ? 'day' : 'days'}`
}

export const getPlanMonths = (length: number, unit?: string): number => {
  if (unit === 'MONTH') return length
  // existing Vodafone day-based plans renew data in 30-day periods (including 365-day plans)
  return Math.round(length / 30)
}

// A 365 Flex plan (product.is_flex, always 12/12 months) has one data allowance for the
// whole year, so it is a single period: priced and described per year, never per month.
export const getMonthlyEquivalentPrice = (
  price: number,
  length: number,
  unit?: string,
  isFlex = false
): number => {
  if (isFlex) return price
  if (unit === 'MONTH') return price / length
  if (DAYS_AS_MONTHS[length]) return price / DAYS_AS_MONTHS[length]
  return (price / length) * 30
}

export const isOnePeriodPlan = (length: number, unit?: string, isFlex = false): boolean => {
  if (isFlex) return true
  if (unit === 'MONTH') return length === 1
  return length === 30
}

// The period a plan's data allowance lasts before it resets: a Flex plan's lasts the year
export const getPeriodLabel = (isFlex = false): string => (isFlex ? 'year' : 'month')

// Plans are priced per month, except a Flex plan, whose one allowance covers its whole year
export const getPricePeriodText = (isFlex = false): string => (isFlex ? 'for 1 year' : 'per month')

export const makeResponsiveColumn = (
  InnerComponent: FunctionComponent<OnlyProps>,
  only?: string
) => (
  // props should be arbitrary
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any
): ReactNode => {
  return <InnerComponent only={only} {...props} />
}
